import ApiService from "../../../services/ApiService";
import HomePuzzleCoinsService from "../../../services/HomePuzzleCoinsService";
import Logger from "../../../services/Logger";

const DEFAULT_BASKET_COUNTS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

export function preferColorBasketCount(allowed, meta) {
    const list = Array.isArray(allowed) && allowed.length > 0 ? allowed : DEFAULT_BASKET_COUNTS;
    const freeMax = Number(meta?.freeMaxCount);
    if (Number.isFinite(freeMax) && freeMax >= 2) {
        if (list.includes(freeMax)) return freeMax;
        const freeAllowed = list.filter((n) => n <= freeMax);
        if (freeAllowed.length > 0) return freeAllowed[freeAllowed.length - 1];
    }
    // Prefer the highest allowed K so the full basket set is shown (e.g. allowed 4,5,6 → 6).
    return list[list.length - 1];
}

/**
 * Per-user Color Basket play (separate from main /home/jigsaw-play).
 * Base path: /home/jigsaw-color-basket-play
 */
const JigsawColorBasketPlayService = {
    /**
     * Metadata (no baskets) or cached baskets for a published jigsaw.
     * GET /home/jigsaw/{jigsawId}/color-baskets[?basketCount=K]
     */
    getColorBaskets: async (jigsawId, basketCount) => {
        if (!jigsawId) {
            throw new Error("Jigsaw id is required");
        }
        const params =
            basketCount != null && basketCount !== ""
                ? { basketCount: Number(basketCount) }
                : undefined;
        const response = await ApiService.getData(
            `/home/jigsaw/${encodeURIComponent(jigsawId)}/color-baskets`,
            params,
        );
        if (response?.code === "FAILURE") {
            throw new Error(response.message || "Failed to load color baskets");
        }
        return response;
    },

    /**
     * Spend puzzle coins to unlock one color basket (UC-02).
     * POST /home/jigsaw/{jigsawId}/color-baskets/unlock
     */
    unlockColorBaskets: async (jigsawId, basketCount, basketIndex) => {
        if (!jigsawId) {
            throw new Error("Jigsaw id is required");
        }
        const k = Number(basketCount);
        if (!Number.isInteger(k) || k < 2) {
            throw new Error("basketCount must be an integer >= 2");
        }
        const idx = Number(basketIndex);
        if (!Number.isInteger(idx) || idx < 0) {
            throw new Error("basketIndex must be an integer >= 0");
        }
        return HomePuzzleCoinsService.unlockColorBasket({
            jigsawId,
            basketCount: k,
            basketIndex: idx,
        });
    },

    /**
     * List existing basket play sessions for status badges.
     * GET /home/jigsaw-color-basket-play/sessions?jigsawId=&basketCount=
     */
    listSessions: async (jigsawId, basketCount) => {
        try {
            if (!jigsawId) {
                return { success: false, error: "jigsawId is required", data: [] };
            }
            const k = Number(basketCount);
            if (!Number.isInteger(k) || k < 1) {
                return { success: false, error: "basketCount is required", data: [] };
            }
            const response = await ApiService.getData("/home/jigsaw-color-basket-play/sessions", {
                jigsawId,
                basketCount: k,
            });
            if (response?.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to load basket sessions",
                    data: [],
                };
            }
            const list = Array.isArray(response)
                ? response
                : Array.isArray(response?.data)
                  ? response.data
                  : Array.isArray(response?.sessions)
                    ? response.sessions
                    : [];
            return { success: true, data: list };
        } catch (error) {
            Logger.error("Error listing color basket sessions:", error);
            return {
                success: false,
                error: error.message || "Failed to load basket sessions",
                data: [],
            };
        }
    },

    /**
     * POST /home/jigsaw-color-basket-play/start
     */
    start: async ({ jigsawId, basketCount, basketIndex, boardPositions }) => {
        try {
            if (!jigsawId) {
                return { success: false, error: "jigsawId is required" };
            }
            if (basketCount == null || basketIndex == null) {
                return { success: false, error: "basketCount and basketIndex are required" };
            }

            const requestBody = {
                jigsawId,
                basketCount: Number(basketCount),
                basketIndex: Number(basketIndex),
            };
            if (boardPositions !== undefined) {
                requestBody.boardPositions = boardPositions;
            }

            const response = await ApiService.postData(
                "/home/jigsaw-color-basket-play/start",
                requestBody,
            );

            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to start basket play session",
                };
            }
            if (response && response.error) {
                return { success: false, error: response.error };
            }

            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error starting color basket play:", error);
            return {
                success: false,
                error: error.message || "Failed to start basket play session",
            };
        }
    },

    /**
     * PUT /home/jigsaw-color-basket-play/progress
     */
    progress: async ({ jigsawId, basketCount, basketIndex, boardPositions, isSolved }) => {
        try {
            if (!jigsawId) {
                return { success: false, error: "jigsawId is required" };
            }
            if (basketCount == null || basketIndex == null) {
                return { success: false, error: "basketCount and basketIndex are required" };
            }

            const requestBody = {
                jigsawId,
                basketCount: Number(basketCount),
                basketIndex: Number(basketIndex),
            };
            if (boardPositions !== undefined) requestBody.boardPositions = boardPositions;
            if (isSolved !== undefined) requestBody.isSolved = isSolved;

            const response = await ApiService.putData(
                "/home/jigsaw-color-basket-play/progress",
                requestBody,
            );

            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to save basket play progress",
                };
            }
            if (response && response.error) {
                return { success: false, error: response.error };
            }

            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error saving color basket play progress:", error);
            return {
                success: false,
                error: error.message || "Failed to save basket play progress",
            };
        }
    },

    /**
     * POST /home/jigsaw-color-basket-play/reset
     * Replace basket layout, clear transferred pieces, mark unsolved.
     */
    reset: async ({ jigsawId, basketCount, basketIndex, boardPositions }) => {
        try {
            if (!jigsawId) {
                return { success: false, error: "jigsawId is required" };
            }
            if (basketCount == null || basketIndex == null) {
                return { success: false, error: "basketCount and basketIndex are required" };
            }
            if (boardPositions == null) {
                return { success: false, error: "boardPositions is required" };
            }

            const response = await ApiService.postData("/home/jigsaw-color-basket-play/reset", {
                jigsawId,
                basketCount: Number(basketCount),
                basketIndex: Number(basketIndex),
                boardPositions,
            });

            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to reset basket play session",
                };
            }
            if (response && response.error) {
                return { success: false, error: response.error };
            }

            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error resetting color basket play:", error);
            return {
                success: false,
                error: error.message || "Failed to reset basket play session",
            };
        }
    },

    /**
     * POST /home/jigsaw-color-basket-play/place-group
     */
    placeGroup: async ({ jigsawId, basketCount, basketIndex, groupId, pieceIndexes }) => {
        try {
            if (!jigsawId) {
                return { success: false, error: "jigsawId is required" };
            }
            if (basketCount == null || basketIndex == null) {
                return { success: false, error: "basketCount and basketIndex are required" };
            }
            if (!groupId && (!Array.isArray(pieceIndexes) || pieceIndexes.length === 0)) {
                return { success: false, error: "groupId or pieceIndexes is required" };
            }

            const requestBody = {
                jigsawId,
                basketCount: Number(basketCount),
                basketIndex: Number(basketIndex),
            };
            if (groupId) requestBody.groupId = groupId;
            if (Array.isArray(pieceIndexes) && pieceIndexes.length > 0) {
                requestBody.pieceIndexes = pieceIndexes.map(Number);
            }

            const response = await ApiService.postData(
                "/home/jigsaw-color-basket-play/place-group",
                requestBody,
            );

            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to place group on main board",
                };
            }
            if (response && response.error) {
                return { success: false, error: response.error };
            }

            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error placing color basket group:", error);
            return {
                success: false,
                error: error.message || "Failed to place group on main board",
            };
        }
    },

    /**
     * POST /home/jigsaw-color-basket-play/deploy
     * Place all basket-board pieces onto the main board; sets playState to DEPLOYED.
     */
    deploy: async ({ jigsawId, basketCount, basketIndex }) => {
        try {
            if (!jigsawId) {
                return { success: false, error: "jigsawId is required" };
            }
            if (basketCount == null || basketIndex == null) {
                return { success: false, error: "basketCount and basketIndex are required" };
            }

            const response = await ApiService.postData("/home/jigsaw-color-basket-play/deploy", {
                jigsawId,
                basketCount: Number(basketCount),
                basketIndex: Number(basketIndex),
            });

            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to deploy basket pieces",
                };
            }
            if (response && response.error) {
                return { success: false, error: response.error };
            }

            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error deploying color basket:", error);
            return {
                success: false,
                error: error.message || "Failed to deploy basket pieces",
            };
        }
    },
};

/**
 * Load baskets for a jigsaw (empty when unavailable).
 * @param {string} jigsawId
 * @param {number} [preferredCount] — when set and allowed, use this K (e.g. screenshot wants 8).
 * @returns {Promise<object[]>}
 */
export async function loadColorBasketsForJigsaw(jigsawId, preferredCount) {
    if (!jigsawId) return [];
    const meta = await JigsawColorBasketPlayService.getColorBaskets(jigsawId);
    if (meta?.available === false) return [];
    const allowed =
        Array.isArray(meta?.allowedCounts) && meta.allowedCounts.length > 0
            ? meta.allowedCounts.map(Number).filter((n) => Number.isFinite(n) && n >= 2)
            : DEFAULT_BASKET_COUNTS;
    let k = preferColorBasketCount(allowed, meta);
    const pref = Number(preferredCount);
    if (Number.isFinite(pref) && pref >= 2) {
        if (allowed.includes(pref)) {
            k = pref;
        } else {
            const le = allowed.filter((n) => n <= pref);
            k = le.length > 0 ? le[le.length - 1] : allowed[0];
        }
    }
    const result = await JigsawColorBasketPlayService.getColorBaskets(jigsawId, k);
    return Array.isArray(result?.baskets) ? result.baskets : [];
}

export default JigsawColorBasketPlayService;
